/**
 * StreamingSTT - Server-side streaming speech recognition engine
 * 
 * Implements SpeechEngine interface using WebSocket connection to server
 * for streaming STT with better reliability and performance.
 */

import { SpeechEngine, SpeechStates } from './SpeechEngine.js';
import micManager from './MicManager.js';
import { SpeechStateMachine } from './SpeechStateMachine.js';

export class StreamingSTT extends SpeechEngine {
    static isSupported() {
        return 'WebSocket' in window && 'AudioContext' in window && 'MediaDevices' in navigator;
    }

    constructor(options = {}) {
        super();
        
        this.options = {
            // Server configuration
            serverUrl: options.serverUrl || process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api',
            wsUrl: options.wsUrl || null,
            
            // Audio configuration
            sampleRate: options.sampleRate || 16000,
            format: options.format || 'pcm',
            language: options.language || 'en-US',
            
            // Connection settings
            reconnectAttempts: options.reconnectAttempts || 3,
            reconnectDelay: options.reconnectDelay || 1000,
            connectionTimeout: options.connectionTimeout || 10000,
            
            ...options
        };
        
        this.stateMachine = new SpeechStateMachine({
            maxRetries: options.maxRetries || 3,
            baseDelay: options.baseDelay || 1000,
            maxDelay: options.maxDelay || 10000
        });
        
        this.isSupported = false;
        this.isInitialized = false;
        this.sessionId = null;
        this.ws = null;
        this.mediaStream = null;
        this.audioContext = null;
        this.processor = null;
        this.audioSource = null;
        this.partialBuffer = '';
        this.isManualStop = false;
        this.reconnectCount = 0;
        this.connectionTimeout = null;
        
        this.initialize();
    }

    /**
     * Initialize the streaming STT engine
     */
    async initialize() {
        try {
            // Check WebSocket support
            if (!window.WebSocket) {
                console.warn('🎤 StreamingSTT: WebSocket not supported');
                this.isSupported = false;
                return;
            }
            
            // Check Web Audio API support
            if (!window.AudioContext && !window.webkitAudioContext) {
                console.warn('🎤 StreamingSTT: Web Audio API not supported');
                this.isSupported = false;
                return;
            }
            
            this.isSupported = true;
            this.isInitialized = true;
            
            console.log('🎤 StreamingSTT: Initialized successfully');
        } catch (error) {
            console.error('🎤 StreamingSTT: Initialization failed:', error);
            this.isSupported = false;
        }
    }

    /**
     * Create a new STT session
     */
    async createSession() {
        try {
            const response = await fetch(`${this.options.serverUrl}/streaming-stt/sessions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    language: this.options.language,
                    sampleRate: this.options.sampleRate,
                    format: this.options.format
                })
            });
            
            if (!response.ok) {
                throw new Error(`Failed to create session: ${response.statusText}`);
            }
            
            const data = await response.json();
            this.sessionId = data.sessionId;
            this.options.wsUrl = `${this.options.serverUrl.replace('http', 'ws')}/streaming-stt/ws/${this.sessionId}`;
            
            console.log('🎤 StreamingSTT: Session created:', this.sessionId);
            return true;
        } catch (error) {
            console.error('🎤 StreamingSTT: Failed to create session:', error);
            return false;
        }
    }

    /**
     * Connect to WebSocket
     */
    async connectWebSocket() {
        return new Promise((resolve, reject) => {
            try {
                this.ws = new WebSocket(this.options.wsUrl);
                
                // Set connection timeout
                this.connectionTimeout = setTimeout(() => {
                    if (this.ws.readyState === WebSocket.CONNECTING) {
                        this.ws.close();
                        reject(new Error('Connection timeout'));
                    }
                }, this.options.connectionTimeout);
                
                this.ws.onopen = () => {
                    console.log('🎤 StreamingSTT: WebSocket connected');
                    clearTimeout(this.connectionTimeout);
                    this.reconnectCount = 0;
                    resolve(true);
                };
                
                this.ws.onmessage = (event) => {
                    this.handleWebSocketMessage(event.data);
                };
                
                this.ws.onclose = (event) => {
                    console.log('🎤 StreamingSTT: WebSocket closed:', event.code, event.reason);
                    clearTimeout(this.connectionTimeout);
                    this.handleWebSocketClose(event);
                };
                
                this.ws.onerror = (error) => {
                    console.error('🎤 StreamingSTT: WebSocket error:', error);
                    clearTimeout(this.connectionTimeout);
                    reject(error);
                };
                
            } catch (error) {
                clearTimeout(this.connectionTimeout);
                reject(error);
            }
        });
    }

    /**
     * Handle errors
     * @param {Error} error - Error object
     */
    handleError(error) {
        console.error('🎤 StreamingSTT: Error occurred:', error);
        
        // Transition to error state
        this.stateMachine.transitionTo(SpeechStates.ERROR, { 
            error: error.message || 'Unknown error',
            reason: 'streaming_error' 
        });
        
        // Emit error event
        if (this.onError) {
            this.onError(error);
        }
    }

    /**
     * Handle WebSocket messages
     */
    handleWebSocketMessage(data) {
        try {
            const message = JSON.parse(data);
            
            switch (message.type) {
                case 'connected':
                    console.log('🎤 StreamingSTT: Connected to session:', message.sessionId);
                    break;
                    
                case 'partial_result':
                    this.handlePartialResult(message);
                    break;
                    
                case 'final_result':
                    this.handleFinalResult(message);
                    break;
                    
                case 'recognition_started':
                    this.stateMachine.transitionTo(SpeechStates.LISTENING, { 
                        reason: 'server_recognition_started' 
                    });
                    break;
                    
                case 'recognition_stopped':
                    this.stateMachine.transitionTo(SpeechStates.ENDPOINTED, { 
                        reason: 'server_recognition_stopped' 
                    });
                    break;
                    
                case 'error':
                    this.handleError({ error: message.error, message: message.message });
                    break;
                    
                case 'pong':
                    // Keep-alive response
                    break;
                    
                default:
                    console.log('🎤 StreamingSTT: Unknown message type:', message.type);
            }
        } catch (error) {
            console.error('🎤 StreamingSTT: Failed to parse WebSocket message:', error);
        }
    }

    /**
     * Handle WebSocket close
     */
    handleWebSocketClose(event) {
        if (this.isManualStop) {
            return; // Expected close
        }
        
        // Attempt to reconnect if not manual stop
        if (this.reconnectCount < this.options.reconnectAttempts) {
            this.reconnectCount++;
            console.log(`🎤 StreamingSTT: Attempting reconnect ${this.reconnectCount}/${this.options.reconnectAttempts}`);
            
            setTimeout(() => {
                this.connectWebSocket().catch(error => {
                    console.error('🎤 StreamingSTT: Reconnect failed:', error);
                    this.handleError(error);
                });
            }, this.options.reconnectDelay * this.reconnectCount);
        } else {
            console.error('🎤 StreamingSTT: Max reconnection attempts reached');
            this.stateMachine.transitionTo(SpeechStates.ERROR, { 
                error: 'connection_lost', 
                reason: 'max_reconnect_attempts' 
            });
            
            // Emit fallback event for SmartSpeechEngine to handle
            this.emit('streaming_fallback_needed', {
                reason: 'connection_lost',
                attempts: this.reconnectCount
            });
        }
    }

    /**
     * Handle partial results
     */
    handlePartialResult(result) {
        this.partialBuffer = result.transcript;
        
        if (this.onPartial) {
            this.onPartial({
                transcript: result.transcript,
                confidence: result.confidence || 0,
                isFinal: false
            });
        }
    }

    /**
     * Handle final results
     */
    handleFinalResult(result) {
        const finalText = result.transcript;
        console.log('🎤 StreamingSTT: Final result:', finalText);
        
        if (this.onFinal) {
            this.onFinal({
                transcript: finalText,
                confidence: result.confidence || 0,
                isFinal: true
            });
        }
        
        // Clear partial buffer
        this.partialBuffer = '';
    }

    /**
     * Setup audio capture and streaming
     */
    async setupAudioCapture() {
        try {
            // Acquire mic exclusively via MicManager (single source of truth)
            await micManager.acquire({
                audio: {
                    sampleRate: this.options.sampleRate,
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });
            // Use MicManager for AudioContext and MediaStream
            this.audioContext = micManager.getAudioContext();
            if (!this.audioContext) {
                throw new Error('AudioContext not available from MicManager');
            }

            // Create audio source using MicManager's validated stream
            this.audioSource = micManager.createMediaStreamSource();
            
            // Create script processor for audio capture
            this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);
            
            this.processor.onaudioprocess = (event) => {
                if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                    const inputBuffer = event.inputBuffer;
                    const inputData = inputBuffer.getChannelData(0);
                    
                    // Convert float32 to int16 PCM
                    const pcmData = new Int16Array(inputData.length);
                    for (let i = 0; i < inputData.length; i++) {
                        pcmData[i] = Math.max(-32768, Math.min(32767, inputData[i] * 32768));
                    }
                    
                    // Send audio frame to server
                    this.ws.send(JSON.stringify({
                        type: 'audio_frame',
                        data: Array.from(pcmData),
                        timestamp: Date.now()
                    }));
                }
            };
            
            // Connect audio pipeline
            this.audioSource.connect(this.processor);
            this.processor.connect(this.audioContext.destination);
            
            console.log('🎤 StreamingSTT: Audio capture setup complete');
            return true;
        } catch (error) {
            console.error('🎤 StreamingSTT: Failed to setup audio capture:', error);
            return false;
        }
    }

    /**
     * Start speech recognition
     * @returns {Promise<boolean>} Success status
     */
    async start() {
        if (!this.isSupported || !this.isInitialized) {
            console.error('🎤 StreamingSTT: Not supported or not initialized');
            return false;
        }

        try {
            this.isManualStop = false;
            this.partialBuffer = '';
            this.stateMachine.resetRetryCount();
            
            // Create session
            const sessionCreated = await this.createSession();
            if (!sessionCreated) {
                throw new Error('Failed to create STT session');
            }
            
            // Connect WebSocket
            await this.connectWebSocket();
            
            // Setup audio capture
            const audioSetup = await this.setupAudioCapture();
            if (!audioSetup) {
                throw new Error('Failed to setup audio capture');
            }
            
            // Start recognition
            this.ws.send(JSON.stringify({
                type: 'start_recognition',
                timestamp: Date.now()
            }));
            
            this.stateMachine.transitionTo(SpeechStates.ACQUIRING_MIC, { 
                reason: 'streaming_start_requested' 
            });
            
            console.log('🎤 StreamingSTT: Started successfully');
            return true;
        } catch (error) {
            console.error('🎤 StreamingSTT: Failed to start:', error);
            this.handleError(error);
            return false;
        }
    }

    /**
     * Stop speech recognition
     * @returns {Promise<boolean>} Success status
     */
    async stop() {
        try {
            this.isManualStop = true;
            this.stateMachine.clearRetryTimeout();
            
            // Stop recognition
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.ws.send(JSON.stringify({
                    type: 'stop_recognition',
                    timestamp: Date.now()
                }));
            }
            
            // Cleanup audio
            this.cleanupAudio();
            
            // Close WebSocket
            if (this.ws) {
                this.ws.close(1000, 'Manual stop');
                this.ws = null;
            }
            
            this.stateMachine.transitionTo(SpeechStates.IDLE, { 
                reason: 'manual_stop' 
            });
            
            console.log('🎤 StreamingSTT: Stopped successfully');
            return true;
        } catch (error) {
            console.error('🎤 StreamingSTT: Failed to stop:', error);
            return false;
        }
    }

    /**
     * Cleanup audio resources
     */
    cleanupAudio() {
        if (this.processor) {
            this.processor.disconnect();
            this.processor = null;
        }
        
        if (this.audioSource) {
            this.audioSource.disconnect();
            this.audioSource = null;
        }
        
        // Don't close AudioContext - MicManager owns it
        this.audioContext = null;
        // Release mic reference owned by this engine
        try {
            micManager.release();
        } catch (e) {
            // ignore
        }
    }

    /**
     * Check if currently listening
     * @returns {boolean} Listening status
     */
    isListening() {
        return this.stateMachine.isListening();
    }

    /**
     * Get current state
     * @returns {string} Current state
     */
    getState() {
        return this.stateMachine.getCurrentState();
    }

    /**
     * Check if speech recognition is supported
     * @returns {boolean} Support status
     */
    isSupported() {
        return this.isSupported;
    }

    /**
     * Get browser compatibility info
     * @returns {Object} Compatibility info
     */
    getBrowserInfo() {
        return {
            hasWebSocket: !!window.WebSocket,
            hasAudioContext: !!(window.AudioContext || window.webkitAudioContext),
            hasMediaDevices: !!navigator.mediaDevices,
            isSupported: this.isSupported,
            isStreamingSTT: true
        };
    }

    /**
     * Cleanup resources
     */
    destroy() {
        this.stop();
        this.cleanupAudio();
        
        // Transition to idle with cleanup reason to avoid transition errors
        if (this.stateMachine.getCurrentState() !== SpeechStates.IDLE) {
            this.stateMachine.transitionTo(SpeechStates.IDLE, { 
                reason: 'cleanup' 
            });
        }
        
        this.stateMachine.destroy();
        super.destroy();
    }
}
